import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  CircularProgress,
  Alert,
  Container,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  Refresh,
  Wifi,
  WifiOff,
  NetworkCheck,
  NetworkPing,
  Router as RouterIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import routerService from '../services/routerService';
import { format } from 'date-fns';
import AddRouterDialog from '../components/AddRouterDialog';
import TestResultDialog from '../components/TestResultDialog';
import RouterDetailDialog from '../components/RouterDetailDialog';
import EditRouterDialog from '../components/EditRouterDialog';
import { useAuth } from '../hooks/useAuth';

type PageLimit = 10 | 50 | 100 | 'all';

interface RouterData {
  id: string;
  name: string;
  host?: string;
  ip_address: string;
  username: string;
  port: number;
  status: 'active' | 'inactive' | 'error';
  description?: string;
  os_version?: string;
  spopdId?: string;
  last_ping?: string;
  spop?: {
    id: string;
    name: string;
    area_code?: string;
    areaCode?: string;
  };
  created_at?: string;
  createdAt?: string;
}

const Routers: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageLimit>(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState<string | null>(null);
  const [pingingRouter, setPingingRouter] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [testResultDialog, setTestResultDialog] = useState<{
    open: boolean;
    result: {
      type: 'connection' | 'ping';
      success: boolean;
      message: string;
      routerName: string;
      routerIp: string;
      responseTime?: number;
      timestamp: Date;
    } | null;
  }>({ open: false, result: null });

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedRouter, setSelectedRouter] = useState<RouterData | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRouter, setEditingRouter] = useState<RouterData | null>(null);

  const { data: routersData, isLoading, error, refetch } = useQuery({
    queryKey: ['routers', page, limit, search, statusFilter],
    queryFn: () =>
      routerService.getRouters(page, limit, search || undefined, {
        status: statusFilter || undefined,
      }),
  });

  const routers: RouterData[] = (routersData?.data?.routers as unknown as RouterData[]) ?? [];

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      routerService.toggleRouterStatus(id, isActive),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['routers'] });
      setActionMessage({ type: 'success', text: res.message });
      setTimeout(() => setActionMessage(null), 3000);
    },
    onError: (err: any) => {
      setActionMessage({
        type: 'error',
        text: err.response?.data?.message || 'Gagal mengubah status router.',
      });
      setTimeout(() => setActionMessage(null), 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => routerService.deleteRouter(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['routers'] });
      setActionMessage({ type: 'success', text: 'Router berhasil dihapus.' });
      setTimeout(() => setActionMessage(null), 3000);
    },
    onError: (err: any) => {
      setActionMessage({
        type: 'error',
        text: err.response?.data?.message || 'Gagal menghapus router.',
      });
      setTimeout(() => setActionMessage(null), 5000);
    },
  });

  React.useEffect(() => {
    setPage(1);
  }, [search, statusFilter, limit]);

  const getRouterIp = (router: RouterData) => router.host || router.ip_address || '-';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'inactive':
        return 'Non-aktif';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string) => {
    return status === 'active' ? <Wifi fontSize="small" /> : <WifiOff fontSize="small" />;
  };

  const handleToggleRouter = (router: RouterData, isActive: boolean) => {
    if (router.status === 'error' && isActive) {
      setActionMessage({
        type: 'error',
        text: 'Router berstatus error. Test koneksi API terlebih dahulu sebelum mengaktifkan.',
      });
      setTimeout(() => setActionMessage(null), 4000);
      return;
    }
    toggleMutation.mutate({ id: router.id, isActive });
  };

  const handleDeleteRouter = (router: RouterData) => {
    if (
      window.confirm(
        `Hapus router "${router.name}"?\nRouter yang masih memiliki pelanggan tidak dapat dihapus.`
      )
    ) {
      deleteMutation.mutate(router.id);
    }
  };

  const handleTestConnection = async (router: RouterData) => {
    setTestingConnection(router.id);
    try {
      const response = await routerService.testExistingRouter(router.id);
      refetch();
      setTestResultDialog({
        open: true,
        result: {
          type: 'connection',
          success: response.success,
          message: response.message || 'Test koneksi selesai',
          routerName: router.name,
          routerIp: getRouterIp(router),
          timestamp: new Date(),
        },
      });
    } catch (error: any) {
      refetch();
      setTestResultDialog({
        open: true,
        result: {
          type: 'connection',
          success: false,
          message: error.response?.data?.message || error.message || 'Gagal test koneksi API',
          routerName: router.name,
          routerIp: getRouterIp(router),
          timestamp: new Date(),
        },
      });
    } finally {
      setTestingConnection(null);
    }
  };

  const handlePingRouter = async (router: RouterData) => {
    setPingingRouter(router.id);
    try {
      const response = await routerService.pingRouter(router.id);
      refetch();
      setTestResultDialog({
        open: true,
        result: {
          type: 'ping',
          success: response.success,
          message: response.message,
          routerName: router.name,
          routerIp: getRouterIp(router),
          responseTime: response.data?.responseTime,
          timestamp: new Date(),
        },
      });
    } catch (error: any) {
      setTestResultDialog({
        open: true,
        result: {
          type: 'ping',
          success: false,
          message: error.response?.data?.message || 'Ping gagal — host tidak dapat dijangkau',
          routerName: router.name,
          routerIp: getRouterIp(router),
          timestamp: new Date(),
        },
      });
    } finally {
      setPingingRouter(null);
    }
  };

  const activeCount = routers.filter((r) => r.status === 'active').length;
  const errorCount = routers.filter((r) => r.status === 'error').length;

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Gagal memuat router: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RouterIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" fontWeight={700}>
              Manajemen Router
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Kelola koneksi MikroTik API untuk PPPoE pelanggan
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button variant="outlined" startIcon={<Refresh />} onClick={() => refetch()}>
            Refresh
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setAddDialogOpen(true)}>
              Tambah Router
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Chip label={`${activeCount} aktif`} color="success" size="small" variant="outlined" />
        <Chip label={`${errorCount} error`} color="error" size="small" variant="outlined" />
        <Chip
          label={`${routersData?.data?.pagination?.total ?? 0} total`}
          size="small"
          variant="outlined"
        />
      </Stack>

      {actionMessage && (
        <Alert severity={actionMessage.type} sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }} variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Cari nama, IP, username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">Semua</MenuItem>
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Non-aktif</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            {(search || statusFilter) && (
              <Button
                size="small"
                onClick={() => {
                  setSearch('');
                  setStatusFilter('');
                }}
              >
                Reset
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nama</TableCell>
                      <TableCell>IP Address</TableCell>
                      <TableCell>Port</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>SPOP</TableCell>
                      <TableCell>OS</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Terakhir Cek</TableCell>
                      <TableCell align="center">Test</TableCell>
                      <TableCell align="right">Aksi</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {routers.length > 0 ? (
                      routers.map((router) => (
                        <TableRow key={router.id} hover>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              {getStatusIcon(router.status)}
                              <Typography variant="body2" fontWeight={600}>
                                {router.name}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{getRouterIp(router)}</TableCell>
                          <TableCell>{router.port || 8728}</TableCell>
                          <TableCell>{router.username}</TableCell>
                          <TableCell>{router.spop?.name || '-'}</TableCell>
                          <TableCell>v{router.os_version || '6'}</TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              {isAdmin ? (
                                <Switch
                                  size="small"
                                  checked={router.status === 'active'}
                                  onChange={(e) => handleToggleRouter(router, e.target.checked)}
                                  disabled={toggleMutation.isPending}
                                />
                              ) : null}
                              <Chip
                                label={getStatusLabel(router.status)}
                                color={getStatusColor(router.status) as 'success' | 'default' | 'error'}
                                size="small"
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {router.last_ping
                              ? format(new Date(router.last_ping), 'dd/MM/yy HH:mm')
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Test API MikroTik">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleTestConnection(router)}
                                    disabled={testingConnection === router.id}
                                  >
                                    {testingConnection === router.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <NetworkCheck fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Ping ICMP">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handlePingRouter(router)}
                                    disabled={pingingRouter === router.id}
                                  >
                                    {pingingRouter === router.id ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <NetworkPing fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Detail">
                              <IconButton size="small" onClick={() => { setSelectedRouter(router); setDetailDialogOpen(true); }}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {isAdmin && (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => { setEditingRouter(router); setEditDialogOpen(true); }}>
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Hapus">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteRouter(router)}
                                    disabled={deleteMutation.isPending}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          Tidak ada data router
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {routersData?.data?.pagination && (
                <TablePagination
                  component="div"
                  count={routersData.data.pagination.total}
                  page={limit === 'all' ? 0 : page - 1}
                  onPageChange={(_, newPage) => setPage(newPage + 1)}
                  rowsPerPage={limit === 'all' ? -1 : limit}
                  onRowsPerPageChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setLimit(val === -1 ? 'all' : (val as 10 | 50 | 100));
                  }}
                  rowsPerPageOptions={[10, 50, 100, { label: 'Semua', value: -1 }]}
                  labelRowsPerPage="Tampilkan:"
                  labelDisplayedRows={({ from, to, count }) =>
                    limit === 'all' ? `Semua ${count} router` : `${from}–${to} dari ${count}`
                  }
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AddRouterDialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} />

      <TestResultDialog
        open={testResultDialog.open}
        result={testResultDialog.result}
        onClose={() => setTestResultDialog({ open: false, result: null })}
      />

      <RouterDetailDialog
        open={detailDialogOpen}
        router={selectedRouter}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedRouter(null);
        }}
      />

      <EditRouterDialog
        open={editDialogOpen}
        router={editingRouter}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingRouter(null);
        }}
      />
    </Container>
  );
};

export default Routers;
